import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';

import NotificationsTable from '@/components/consumer/NotificationsTable';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import { ProfileCancelIcon } from '@/components/ui/svg/ProfileIcons';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { formatTimestamp } from '@/components/utils/utils';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollection } from '@/interfaces/api/collection/ICollection';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const NotificationsPage = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchQuery, setSearchQuery] = useState('');
	const [activeTab, setActiveTab] = useState<'notifications' | 'favourites'>(
		'notifications',
	);

	const filter = {
		size: pageSize,
		number: pageNumber,
	};

	const {
		data: notifications,
		isLoading: notificationsLoading,
		isError: notificationsError,
		error: notificationsFetchError,
		refetch: notificationsRefetch,
	} = useQuery<ICollection<INotifications>>({
		queryKey: [ApiQueryKeys.getAllNotifications, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllNotifications({
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	const {
		data: favourites,
		isLoading: favouritesLoading,
		refetch: favouritesRefetch,
	} = useQuery<ICollection<INotifications>>({
		queryKey: ['favourites', pageSize, pageNumber],
		queryFn: () => haggleApiService.getFavourites({ params: filter }),
		enabled: activeTab === 'favourites',
		refetchOnWindowFocus: false,
	});

	if (notificationsError) {
		if (notificationsFetchError instanceof Error) {
			notificationService.error(notificationsFetchError.message);
		} else {
			notificationService.error('Unknown error fetching notifications');
		}
	}

	const handlePaginationOptions = (
		collection: ICollection<INotifications>,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const deleteNotification = (id: number) => {
		haggleApiService
			.deleteNotificationById(id)
			.then(() => {
				notificationsRefetch();
				notificationService.success('Notification deleted successfully');
			})
			.catch(() => {
				notificationService.error('Error deleting notification');
			});
	};

	const deleteFavourite = (favouritedUserId: string) => {
		haggleApiService
			.deleteFavouriteNotificationById(favouritedUserId)
			.then(() => {
				favouritesRefetch();
				notificationService.success('Favourite removed successfully');
			})
			.catch(() => {
				notificationService.error('Error removing favourite');
			});
	};

	const columnHelper = createColumnHelper<INotifications>();

	const columns = [
		columnHelper.accessor('productName', {
			cell: (info) => (
				<div className="min-w-[150px] w-[150px] truncate">
					{info.getValue()}
				</div>
			),
			header: () => <div className="min-w-[150px]">Product Name</div>,
		}),
		columnHelper.accessor('haggleStartDate', {
			cell: (info) => (
				<div className="min-w-[200px]">
					<span className="block">{formatTimestamp(info.getValue()).date}</span>
					<span className="block">{formatTimestamp(info.getValue()).time}</span>
				</div>
			),
			header: () => <div className="min-w-[200px]">Haggle Date</div>,
		}),
		columnHelper.accessor('sendAt', {
			cell: (info) => (
				<div className="min-w-[200px]">
					<span className="block">{formatTimestamp(info.getValue()).date}</span>
					<span className="block">{formatTimestamp(info.getValue()).time}</span>
				</div>
			),
			header: () => <div className="min-w-[200px]">Notification Time</div>,
		}),
		columnHelper.accessor('id', {
			cell: (info) => (
				<div className="min-w-[60px] flex justify-center">
					<button
						onClick={() => deleteNotification(info.getValue())}
						className="p-1 rounded-md"
					>
						<ProfileCancelIcon />
					</button>
				</div>
			),
			header: () => <div className="min-w-[60px]"></div>,
		}),
	];

	const favouriteColumns = [
		columnHelper.accessor('favouritedUserId', {
			cell: (info) => (
				<div className="min-w-[150px] w-[150px] truncate">
					{info.getValue()}
				</div>
			),
			header: () => <div className="min-w-[150px]">Merchant</div>,
		}),
		columnHelper.accessor('haggleStartDate', {
			cell: (info) => (
				<div className="min-w-[200px]">
					<span className="block">{formatTimestamp(info.getValue()).date}</span>
					<span className="block">{formatTimestamp(info.getValue()).time}</span>
				</div>
			),
			header: () => <div className="min-w-[200px]">Haggle Date</div>,
		}),
		columnHelper.accessor('sent', {
			cell: (info) => (
				<div className="min-w-[150px] text-center">
					{info.getValue() ? (
						<span className="text-purpleBrand-500 font-semibold">Yes</span>
					) : (
						<span className="text-red-500 font-semibold">No</span>
					)}
				</div>
			),
			header: () => <div className="min-w-[150px] text-center">Sent</div>,
		}),
		columnHelper.accessor('id', {
			cell: (info) => {
				const favouritedUserId = info.row.original.favouritedUserId;

				return (
					<div className="min-w-[60px] flex justify-center">
						<button
							onClick={() => deleteFavourite(favouritedUserId!)}
							className="p-1 rounded-md"
						>
							<ProfileCancelIcon />
						</button>
					</div>
				);
			},
			header: () => <div className="min-w-[60px]"></div>,
		}),
	];

	return (
		<div className="flex flex-col justify-center items-center w-full">
			<div className="flex justify-start gap-4 mb-4 w-full px-4">
				<button
					className={`px-4 py-2 rounded ${
						activeTab === 'notifications'
							? 'bg-purpleBrand-500 hover:bg-purpleBrand-600  text-white'
							: 'bg-gray-200'
					}`}
					onClick={() => setActiveTab('notifications')}
				>
					Notifications
				</button>
				<button
					className={`px-4 py-2 rounded ${
						activeTab === 'favourites'
							? 'bg-purpleBrand-500 hover:bg-purpleBrand-600  text-white'
							: 'bg-gray-200'
					}`}
					onClick={() => setActiveTab('favourites')}
				>
					Favourite Haggles
				</button>
			</div>
			{(notificationsLoading ||
				(activeTab === 'favourites' && favouritesLoading)) && (
				<LoadingSkeletonModalTable />
			)}
			{activeTab === 'notifications' &&
				notifications &&
				!notificationsLoading && (
					<NotificationsTable
						notifications={notifications.data}
						paginationOption={handlePaginationOptions(notifications)}
						columns={columns}
						refetch={notificationsRefetch}
						query={searchQuery}
						setQuery={setSearchQuery}
					/>
				)}

			{activeTab === 'favourites' && favourites && !favouritesLoading && (
				<NotificationsTable
					notifications={favourites.data}
					paginationOption={handlePaginationOptions(favourites)}
					columns={favouriteColumns}
					refetch={favouritesRefetch}
					query={searchQuery}
					setQuery={setSearchQuery}
				/>
			)}
		</div>
	);
};

export default NotificationsPage;
